import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { CloudSun, AlertTriangle, Wind, Droplets, Sun, Thermometer, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const WEATHER_ICONS = {
  'sun': '☀️', 'cloud-sun': '⛅', 'cloud': '☁️', 'cloud-fog': '🌫️',
  'cloud-drizzle': '🌧️', 'cloud-rain': '🌧️', 'snowflake': '❄️',
  'cloud-rain-wind': '⛈️', 'cloud-lightning': '⚡',
};

export default function Weather() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    Promise.all([
      api.get(`/weather/${selectedProject}`),
      api.get(`/weather/alerts/${selectedProject}`),
    ]).then(([w, a]) => {
      setData(w.data);
      setAlerts(a.data.alerts || []);
    }).catch(e => {
      const msg = e.response?.data?.message || e.response?.data?.error || 'Error obteniendo datos';
      toast.error(msg);
      setData(null);
    }).finally(() => setLoading(false));
  }, [selectedProject]);

  const getDayName = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const today = new Date(); today.setHours(12, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (d.toDateString() === today.toDateString()) return 'Hoy';
    if (d.toDateString() === tomorrow.toDateString()) return 'Mañana';
    return d.toLocaleDateString('es', { weekday: 'long' });
  };

  const getWorkCondition = (day) => {
    if (day.wind_speed_kmh > 40 || day.temp_max > 40) return { label: 'No trabajar', color: '#EF4444', bg: '#EF444420' };
    if (day.rain_probability > 70 || day.wind_speed_kmh > 25 || day.temp_max > 38) return { label: 'Precaución', color: '#F59E0B', bg: '#F59E0B20' };
    return { label: 'Favorable', color: '#10B981', bg: '#10B98120' };
  };

  return (
    <div className="weather-page">
      <div className="page-header">
        <div><h1><CloudSun size={24} /> Meteorología</h1><p className="subtitle">Previsión para obras</p></div>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Seleccionar proyecto...</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        {data?.cached && <span className="wx-cached-badge">Datos en caché</span>}
      </div>

      {loading && <div className="wx-loading">Cargando previsión...</div>}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="wx-alerts">
          {alerts.map((a, i) => (
            <div key={i} className={`wx-alert ${a.level}`}>
              <AlertTriangle size={16} />
              <span className="wx-alert-date">{getDayName(a.date)}:</span>
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Forecast cards */}
      {data?.forecast && (
        <div className="wx-forecast-grid">
          {data.forecast.map((day, i) => {
            const cond = getWorkCondition(day);
            return (
              <div key={i} className={`wx-day-card ${i === 0 ? 'today' : ''}`}>
                <div className="wx-day-header">
                  <span className="wx-day-name">{getDayName(day.date)}</span>
                  <span className="wx-day-date">{new Date(day.date + 'T12:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}</span>
                </div>

                <div className="wx-day-icon">{WEATHER_ICONS[day.icon] || '🌤️'}</div>
                <div className="wx-day-desc">{day.description}</div>

                <div className="wx-day-temps">
                  <span className="wx-temp-max">{Math.round(day.temp_max)}°</span>
                  <span className="wx-temp-min">{Math.round(day.temp_min)}°</span>
                </div>

                <div className="wx-day-details">
                  <div className="wx-detail"><Droplets size={12} /> {day.rain_probability}%</div>
                  <div className="wx-detail"><Wind size={12} /> {Math.round(day.wind_speed_kmh)} km/h</div>
                  <div className="wx-detail"><Sun size={12} /> UV {day.uv_index}</div>
                  {day.precipitation_mm > 0 && <div className="wx-detail">💧 {day.precipitation_mm}mm</div>}
                </div>

                <div className="wx-day-condition" style={{ background: cond.bg, color: cond.color }}>
                  {cond.label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.project && (
        <div className="wx-project-info">
          📍 {data.project.name} — {data.project.address || ''} {data.project.city || ''}
          <span className="wx-coords">({data.project.latitude}, {data.project.longitude})</span>
        </div>
      )}

      {!selectedProject && (
        <div className="empty-state"><CloudSun size={48} /><h3>Selecciona un proyecto</h3><p>Verás la previsión meteorológica de 7 días para la ubicación de la obra</p></div>
      )}
    </div>
  );
}
